/**
 * SuggestedSearchesV2 — redesigned editor for the curated list of
 * "Suggested searches" the app shows on an empty search query.
 *
 * Each row is a SuggestedSearchSortableItemV2 with three selects
 * (object type, object name/title, search behavior). Drag-to-reorder
 * is local-only until the user clicks Save (parity with v1).
 *
 * Same `suggestedSearchAction` plumbing as v1:
 *   fetchAvailableOption / fetchSuggestedSearches /
 *   addOrEditSuggestedSearch / deleteSuggesteedSearch (sic).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  addOrEditSuggestedSearch,
  deleteSuggesteedSearch,
  fetchAvailableOption,
  fetchSuggestedSearches,
} from '../../../actions/suggestedSearchAction';
import { Button } from '../../redesign';
import { toast } from '../../../utils/v2Toast';
import SuggestedSearchSortableItemV2 from './SuggestedSearchSortableItemV2';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

/* Stable dnd-kit id. Existing rows have a server-assigned `id`; new
 * rows haven't been persisted yet so we mint a `new-…` string. */
function rowId(item) {
  return item.id ?? item._tempId;
}

export default function SuggestedSearchesV2() {
  const dispatch = useDispatch();
  const suggested_search_options = useSelector(
    (state) => state.search.suggested_search_options,
  );
  const suggested_search_data = useSelector(
    (state) => state.search.suggested_search_data,
  );

  const [items, setItems] = useState([]);
  const [edited, setEdited] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    dispatch(fetchAvailableOption());
    dispatch(fetchSuggestedSearches());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(suggested_search_data)) {
      setItems(
        suggested_search_data.map((row) => ({
          ...row,
          _tempId: row.id ? undefined : `new-${Math.random().toString(36).slice(2)}`,
        })),
      );
    } else {
      setItems([]);
    }
  }, [suggested_search_data]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((it) => rowId(it) === active.id);
      const newIndex = prev.findIndex((it) => rowId(it) === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
    setEdited(true);
  }, []);

  const handleChange = useCallback((e, index) => {
    const { value, prop } = e;
    setItems((prev) => {
      const next = [...prev];
      const item = { ...next[index] };
      // Changing the object type invalidates the previous object_id.
      if (prop === 'object_type' && item[prop] !== value) {
        item.object_id = '';
      }
      item[prop] = value;
      next[index] = item;
      return next;
    });
    setEdited(true);
  }, []);

  const handleDelete = useCallback(
    (suggested_search_id, index) => {
      if (suggested_search_id) {
        dispatch(deleteSuggesteedSearch(suggested_search_id));
      }
      setItems((prev) => prev.filter((_, i) => i !== index));
      setEdited(true);
    },
    [dispatch],
  );

  const handleAdd = useCallback(() => {
    setItems((prev) => {
      const hasUnsaved = prev.some((it) => it.id === undefined);
      if (hasUnsaved) {
        toast.error('Please save the current new item before adding another.');
        return prev;
      }
      return [
        ...prev,
        {
          object_id: '',
          object_type: '',
          search_behavior: '',
          _tempId: `new-${Math.random().toString(36).slice(2)}`,
        },
      ];
    });
    setEdited(true);
  }, []);

  const handleSave = useCallback(
    async (e) => {
      e?.preventDefault?.();
      const invalid = items.find(
        (it) => !it.object_id || !it.object_type || !it.search_behavior,
      );
      if (invalid) {
        toast.error('Please fill all missing data for the suggested search.');
        return;
      }
      // Re-stamp sort_order from row position and strip _tempId.
      const payload = items.map((it, idx) => {
        const { _tempId, ...rest } = it;
        return { ...rest, sort_order: idx };
      });
      try {
        await dispatch(addOrEditSuggestedSearch(JSON.stringify(payload)));
        setEdited(false);
        toast.success('Suggested searches updated!');
      } catch (_err) {
        toast.error('Failed to update suggested searches. Please try again.');
      }
    },
    [dispatch, items],
  );

  const isLoading = !suggested_search_options;

  return (
    <div>
      <div className="text-text-muted" style={{ fontSize: 11.5, margin: '0 4px 8px' }}>
        Drag any row to reorder
      </div>

      {isLoading ? (
        <div
          className="bg-cream border border-sand text-text-muted italic text-center"
          style={{ borderRadius: 10, padding: '32px 16px', fontSize: 13 }}
        >
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div
          className="bg-cream border border-sand text-text-muted italic text-center"
          style={{ borderRadius: 10, padding: '32px 16px', fontSize: 13 }}
        >
          No suggested searches yet — click "Add new" to create one.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map(rowId)}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map((it, idx) => (
                <SuggestedSearchSortableItemV2
                  key={rowId(it)}
                  id={rowId(it)}
                  data={it}
                  data_options={suggested_search_options}
                  el_index={idx}
                  handleChange={handleChange}
                  handleDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div
        className="flex items-center"
        style={{ gap: 8, marginTop: 16, justifyContent: 'space-between' }}
      >
        <Button variant="ghost" leadingIcon={PlusSign} onClick={handleAdd}>
          Add new
        </Button>
        <Button
          variant="coral"
          leadingIcon={SaveIconSvg}
          onClick={handleSave}
          disabled={!edited}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
